import { APIProvider } from "@vis.gl/react-google-maps"
import { useTranslation } from "react-i18next"

interface GoogleMapsProviderProps {
  children: React.ReactNode
}

/** Raw Vite env value; may be empty when maps are not configured. */
export const GOOGLE_MAPS_API_KEY = (
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined
)?.trim()

export const GOOGLE_MAPS_MAP_ID = (
  import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined
)?.trim()

/** True when a non-empty Maps API key is available for APIProvider. */
export const isGoogleMapsConfigured = (): boolean =>
  Boolean(GOOGLE_MAPS_API_KEY)

let warnedMissingKey = false

export const GoogleMapsProvider = ({ children }: GoogleMapsProviderProps) => {
  const { i18n } = useTranslation()

  // Without a key, skip APIProvider so non-map pages still work.
  // Map surfaces must check isGoogleMapsConfigured() and show a fallback.
  if (!isGoogleMapsConfigured()) {
    if (import.meta.env.DEV && !warnedMissingKey) {
      warnedMissingKey = true
      console.warn(
        "[GoogleMaps] VITE_GOOGLE_MAPS_API_KEY is empty. Map pages will show a fallback until you set the key in FrontEnd/.env and restart Vite.",
      )
    }
    return <>{children}</>
  }

  // Get language and region with fallbacks
  const language =
    i18n.language === "ua" || i18n.language === "uk" ? "uk" : "en"
  const region = language === "uk" ? "UA" : "US"

  return (
    <APIProvider
      apiKey={GOOGLE_MAPS_API_KEY!}
      language={language}
      region={region}
      libraries={["places", "drawing", "geometry", "geocoding"]}
    >
      {children}
    </APIProvider>
  )
}
